import React, { useEffect, useState } from "react";
import BN from "bn.js";
import { PublicKey } from "@solana/web3.js";
import { TOKEN_PROGRAM_ID, createBurnInstruction } from "@solana/spl-token";
import { TxVersion } from "@raydium-io/raydium-sdk-v2";
import { fromBaseUnits, toBaseUnits } from "@oxminter/shared/amounts";
import { useApp } from "../state";
import { Addr, Field, Note, RequireToken, RequireWallet } from "../components/common";
import { ataFor, burnIx, readMintFacts } from "../lib/token";
import { assertTxSucceeded, buildSimulateSend, readTokenBalanceRaw } from "../lib/tx";
import { cpmmLockPrograms, cpmmPrograms } from "@oxminter/shared/feeconfig";
import { loadRaydium } from "../lib/raydium";
import { recordEvent as postAuditEvent } from "../lib/api";

/** Typing a phrase is friction on purpose: these three actions cannot be undone. */
function ConfirmPhrase(props: {
  phrase: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
}) {
  return (
    <Field label={`Type ${props.phrase} to confirm`}>
      <input
        data-testid={props.testid}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.phrase}
      />
    </Field>
  );
}

export default function Supply() {
  return (
    <RequireWallet>
      <RequireToken>
        <BurnTokens />
        <LpTools />
        <FeeClaim />
      </RequireToken>
    </RequireWallet>
  );
}

function BurnTokens() {
  const { connection, net, wallet, token, fees, say } = useApp();
  const [amount, setAmount] = useState("");
  const [confirm, setConfirm] = useState("");
  const [balanceRaw, setBalanceRaw] = useState<bigint | null>(null);
  const [supplyRaw, setSupplyRaw] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [after, setAfter] = useState<{ supplyBefore: string; supplyAfter: string; sig: string } | null>(null);

  const load = React.useCallback(async () => {
    if (!wallet || !token) return;
    const mint = new PublicKey(token.mint);
    setBalanceRaw(await readTokenBalanceRaw(connection, ataFor(mint, wallet.publicKey)));
    setSupplyRaw((await readMintFacts(connection, mint)).supplyRaw);
  }, [connection, wallet, token]);

  useEffect(() => {
    void load();
  }, [load]);

  let amountRaw: BN | null = null;
  let amountError: string | null = null;
  try {
    if (amount.trim()) amountRaw = toBaseUnits(amount, token!.decimals);
  } catch (e) {
    amountError = (e as Error).message;
  }
  const exceedsBalance =
    amountRaw !== null && balanceRaw !== null && BigInt(amountRaw.toString()) > balanceRaw;

  async function burn() {
    if (!wallet || !token || !amountRaw) return;
    setBusy(true);
    setAfter(null);
    try {
      const mint = new PublicKey(token.mint);
      const before = (await readMintFacts(connection, mint)).supplyRaw;
      const { signature } = await buildSimulateSend({
        connection,
        wallet,
        fees,
        label: "Burn tokens",
        onProgress: (m) => say("info", m),
        instructions: [burnIx(mint, wallet.publicKey, BigInt(amountRaw.toString()))],
      });
      const afterSupply = (await readMintFacts(connection, mint)).supplyRaw;
      setAfter({ supplyBefore: before, supplyAfter: afterSupply, sig: signature });
      postAuditEvent({
        action: "burn",
        cluster: net.cluster,
        mint: mint.toBase58(),
        signature,
        wallet: wallet.publicKey.toBase58(),
        detail: `${amount} ${token.symbol} burned; supply ${before} -> ${afterSupply}`,
      });
      say(
        "ok",
        `Burned ${amount} ${token.symbol}. Supply ${before} -> ${afterSupply} (raw).`
      );
      setAmount("");
      setConfirm("");
      await load();
    } catch (e) {
      say("error", `Burn failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const ready =
    amountRaw !== null &&
    !amountRaw.isZero() &&
    !exceedsBalance &&
    !amountError &&
    confirm === "BURN";

  return (
    <div className="card">
      <h2>Burn {token!.symbol}</h2>
      <p className="sub">
        Permanently destroys tokens from your own account and reduces total supply. There is no
        recovery, and no authority can reverse it.
      </p>
      <dl className="kv" style={{ marginBottom: 14 }}>
        <dt>Your balance</dt>
        <dd data-testid="burn-balance">
          {balanceRaw === null ? "—" : fromBaseUnits(new BN(balanceRaw.toString()), token!.decimals)}
        </dd>
        <dt>Total supply</dt>
        <dd data-testid="burn-supply">
          {supplyRaw === null ? "—" : fromBaseUnits(new BN(supplyRaw), token!.decimals)}
        </dd>
      </dl>

      <Field label={`Amount to burn (${token!.symbol})`}>
        <input data-testid="in-burn" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      {amountError ? <Note kind="danger"><p>{amountError}</p></Note> : null}
      {exceedsBalance ? (
        <Note kind="danger"><p>That is more than your account holds.</p></Note>
      ) : null}

      <ConfirmPhrase phrase="BURN" value={confirm} onChange={setConfirm} testid="in-burnconfirm" />
      <button className="danger" onClick={burn} disabled={busy || !ready} data-testid="btn-burn">
        {busy ? "Burning…" : "Burn permanently"}
      </button>

      {after ? (
        <Note kind="ok">
          <p>
            Burned — <Addr value={after.sig} kind="tx" />
          </p>
          <p data-testid="burn-result">
            Supply {after.supplyBefore} → {after.supplyAfter} (raw units, read back from the mint)
          </p>
        </Note>
      ) : null}
    </div>
  );
}

/* The Fee Key NFT minted by a Burn & Earn lock is the only way back to the
   fees, and it is returned once, in a transaction result that scrolls away.
   Remembering it per pool is a convenience, not a source of truth — the chain
   is — but losing it costs real money. */
function feeKeyStorageKey(poolId: string) {
  return `burn-and-earn-feekeys:${poolId}`;
}

function loadFeeKeys(poolId?: string): string[] {
  if (!poolId) return [];
  try {
    const raw = localStorage.getItem(feeKeyStorageKey(poolId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function saveFeeKey(poolId: string, nftMint: string): string[] {
  const next = Array.from(new Set([...loadFeeKeys(poolId), nftMint]));
  try {
    localStorage.setItem(feeKeyStorageKey(poolId), JSON.stringify(next));
  } catch {
    /* storage unavailable; the value is still shown in the log and on screen */
  }
  return next;
}

function LpTools() {
  const { connection, net, wallet, token, fees, say } = useApp();
  const [lpRaw, setLpRaw] = useState<bigint | null>(null);
  const [burnAmount, setBurnAmount] = useState("");
  const [confirm, setConfirm] = useState("");
  const [lockAmount, setLockAmount] = useState("");
  const [lockConfirm, setLockConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  // The Fee Key NFT is the only handle on a locked position. Losing it means
  // the fees are unreachable, so it is surfaced immediately and remembered.
  const [feeKeys, setFeeKeys] = useState<string[]>(() => loadFeeKeys(token?.poolId));
  const [harvestNft, setHarvestNft] = useState("");
  const [harvestAmount, setHarvestAmount] = useState("");

  useEffect(() => setFeeKeys(loadFeeKeys(token?.poolId)), [token?.poolId]);

  const load = React.useCallback(async () => {
    if (!wallet || !token?.lpMint) return;
    try {
      setLpRaw(
        await readTokenBalanceRaw(connection, ataFor(new PublicKey(token.lpMint), wallet.publicKey))
      );
    } catch (e) {
      // Silently leaving this at "—" is how a NaN ends up in a burn amount.
      say("error", `Could not read LP balance: ${(e as Error).message}`);
      setLpRaw(null);
    }
  }, [connection, wallet, token, say]);

  useEffect(() => {
    void load();
  }, [load]);

  async function burnLp() {
    if (!wallet || !token?.lpMint) return;
    setBusy(true);
    try {
      const lpMint = new PublicKey(token.lpMint);
      const amount = BigInt(burnAmount.trim());
      const { signature } = await buildSimulateSend({
        connection,
        wallet,
        fees,
        label: "Burn LP",
        onProgress: (m) => say("info", m),
        instructions: [
          createBurnInstruction(
            ataFor(lpMint, wallet.publicKey),
            lpMint,
            wallet.publicKey,
            amount,
            [],
            TOKEN_PROGRAM_ID
          ),
        ],
      });
      postAuditEvent({
        action: "lp_burn",
        cluster: net.cluster,
        mint: token.lpMint,
        pool_id: token.poolId,
        signature,
        wallet: wallet.publicKey.toBase58(),
        detail: `${amount} LP burned`,
      });
      say("ok", `Burned ${amount} LP tokens: ${signature}`);
      setBurnAmount("");
      setConfirm("");
      await load();
    } catch (e) {
      say("error", `LP burn failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function lockLp() {
    if (!wallet || !token?.poolId) return;
    setBusy(true);
    try {
      const raydium = await loadRaydium(connection, net.cluster, wallet);
      const { poolInfo, poolKeys } = await raydium.cpmm.getPoolInfoFromRpc(token.poolId);
      const lock = cpmmLockPrograms(net.cluster);
      say("info", "Burn & Earn lock: awaiting wallet approval");
      const { execute, extInfo } = await raydium.cpmm.lockLp({
        poolInfo,
        poolKeys,
        // Explicit, because the SDK otherwise reaches for the mainnet locker
        // whichever cluster the instance was loaded with.
        programId: lock.programId,
        authProgram: lock.authProgram,
        lpAmount: new BN(lockAmount.trim()),
        withMetadata: true,
        txVersion: TxVersion.V0,
        computeBudgetConfig: {
          units: fees.computeUnitLimit,
          microLamports: fees.computeUnitPriceMicroLamports,
        },
      });
      // The SDK types this generic slot wrongly (it passes the ext-info type
      // where the TxVersion belongs), so extInfo widens to Record<string, any>.
      // Read the field defensively rather than trusting the shape.
      const nftMint = (extInfo as { nftMint?: { toBase58(): string } })?.nftMint?.toBase58();
      const { txId } = await execute({ sendAndConfirm: true, skipPreflight: false });
      await assertTxSucceeded(connection, txId, "Burn & Earn lock");
      if (nftMint) {
        const next = saveFeeKey(token.poolId, nftMint);
        setFeeKeys(next);
        setHarvestNft(nftMint);
        postAuditEvent({
          action: "lp_lock",
          cluster: net.cluster,
          mint: token.lpMint,
          pool_id: token.poolId,
          signature: txId,
          wallet: wallet.publicKey.toBase58(),
          detail: `Burn & Earn lock, fee key ${nftMint}`,
        });
        say("ok", `LP locked permanently. Fee Key NFT: ${nftMint}`);
        say("warn", "That NFT is the only claim on these fees. Whoever holds it collects them.");
      } else {
        say(
          "warn",
          `LP locked (${txId}), but the SDK returned no Fee Key NFT mint. Find it in the transaction before you rely on claiming fees.`
        );
      }
      setLockAmount("");
      setLockConfirm("");
      await load();
    } catch (e) {
      say("error", `Burn & Earn lock failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function harvestLocked() {
    if (!wallet || !token?.poolId) return;
    setBusy(true);
    try {
      const raydium = await loadRaydium(connection, net.cluster, wallet);
      const { poolInfo, poolKeys } = await raydium.cpmm.getPoolInfoFromRpc(token.poolId);
      const lock = cpmmLockPrograms(net.cluster);
      const cp = cpmmPrograms(net.cluster);
      say("info", "Harvest locked-LP fees: awaiting wallet approval");
      const { execute } = await raydium.cpmm.harvestLockLp({
        poolInfo,
        poolKeys,
        programId: lock.programId,
        authProgram: lock.authProgram,
        // Same cluster-blindness as the locker itself, one level deeper: the
        // locker CPIs into cp-swap, and the SDK hands it the MAINNET cp-swap
        // id regardless of cluster. On devnet the locker rejects the call with
        // InvalidProgramId (3008) before anything is signed. Verified against a
        // local validator running the devnet programs.
        cpmmProgram: { programId: cp.programId, authProgram: cp.authority },
        nftMint: new PublicKey(harvestNft.trim()),
        lpFeeAmount: new BN(harvestAmount.trim()),
        txVersion: TxVersion.V0,
        computeBudgetConfig: {
          units: fees.computeUnitLimit,
          microLamports: fees.computeUnitPriceMicroLamports,
        },
      });
      const { txId } = await execute({ sendAndConfirm: true, skipPreflight: false });
      await assertTxSucceeded(connection, txId, "Harvest locked LP");
      say("ok", `Harvested fees from the locked position: ${txId}`);
      setHarvestAmount("");
      await load();
    } catch (e) {
      say("error", `Harvest failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!token?.lpMint) {
    return (
      <div className="card">
        <h2>LP position</h2>
        <div className="empty">No pool loaded for this token.</div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>LP position</h2>
      <p className="sub">
        You hold the LP tokens for this pool. Until you lock or burn them you can withdraw the
        liquidity at any time.
      </p>

      <Note kind="warn">
        <p>
          <strong>Two permanent options that are not the same thing.</strong> Burning LP tokens
          destroys your claim outright: the liquidity can never be withdrawn by anyone, and you
          give up every future trading fee. Raydium&apos;s <strong>Burn &amp; Earn</strong> also
          locks the liquidity permanently — the LP tokens go to the locker program and cannot be
          withdrawn — but it mints you a transferable Fee Key NFT that keeps the fee stream
          claimable. Whoever ends up holding that NFT collects those fees.
        </p>
        <p>
          Both are irreversible. Neither is reversible by Raydium, and neither is what most people
          mean by a time-limited &ldquo;lock&rdquo;. Say which one you used.
        </p>
      </Note>

      <dl className="kv" style={{ marginBottom: 14 }}>
        <dt>LP mint</dt>
        <dd><Addr value={token.lpMint} /></dd>
        <dt>Your LP balance (raw)</dt>
        <dd data-testid="lp-balance">{lpRaw === null ? "loading…" : lpRaw.toString()}</dd>
      </dl>

      <h3>Burn &amp; Earn — permanent LP lock</h3>
      <p className="sub" style={{ margin: "0 0 10px" }}>
        Sends LP tokens to Raydium&apos;s locker program and mints you a Fee Key NFT. The
        liquidity can never be withdrawn again; the fees remain claimable below.
      </p>
      <Field label="LP amount to lock (raw units)" req="irreversible">
        <input
          data-testid="in-lplock"
          value={lockAmount}
          onChange={(e) => setLockAmount(e.target.value)}
        />
      </Field>
      <ConfirmPhrase
        phrase="LOCK FOREVER"
        value={lockConfirm}
        onChange={setLockConfirm}
        testid="in-lplockconfirm"
      />
      <button
        className="danger"
        onClick={lockLp}
        disabled={busy || !lockAmount.trim() || lockConfirm !== "LOCK FOREVER"}
        data-testid="btn-lplock"
      >
        Lock permanently
      </button>

      <h3>Harvest fees from locked liquidity</h3>
      {feeKeys.length ? (
        <dl className="kv" style={{ marginBottom: 10 }}>
          <dt>Fee Key NFTs from this browser</dt>
          <dd data-testid="feekeys">
            {feeKeys.map((k) => (
              <div key={k}>
                <Addr value={k} />
              </div>
            ))}
          </dd>
        </dl>
      ) : (
        <p className="sub" style={{ margin: "0 0 10px" }}>
          No Fee Key NFT recorded in this browser. If you locked from elsewhere, paste its mint.
        </p>
      )}
      <Field label="Fee Key NFT mint">
        <input
          data-testid="in-harvestnft"
          value={harvestNft}
          onChange={(e) => setHarvestNft(e.target.value)}
          placeholder="NFT mint address"
        />
      </Field>
      <Field
        label="LP fee amount to claim (raw units)"
        hint="How much of the accrued LP value to convert back into the underlying tokens."
      >
        <input
          data-testid="in-harvestamt"
          value={harvestAmount}
          onChange={(e) => setHarvestAmount(e.target.value)}
        />
      </Field>
      <button
        onClick={harvestLocked}
        disabled={busy || !harvestNft.trim() || !harvestAmount.trim()}
        data-testid="btn-harvest"
      >
        Harvest fees
      </button>

      <h3>Burn LP permanently</h3>
      <Field label="LP amount to burn (raw units)">
        <input
          data-testid="in-lpburn"
          value={burnAmount}
          onChange={(e) => setBurnAmount(e.target.value)}
        />
      </Field>
      <ConfirmPhrase
        phrase="BURN LP"
        value={confirm}
        onChange={setConfirm}
        testid="in-lpburnconfirm"
      />
      <button
        className="danger"
        onClick={burnLp}
        disabled={busy || !burnAmount.trim() || confirm !== "BURN LP"}
        data-testid="btn-lpburn"
      >
        Burn LP permanently
      </button>
    </div>
  );
}

function FeeClaim() {
  const { connection, net, wallet, token, fees, say } = useApp();
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<{ enabled: boolean; a: string; b: string } | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      if (!wallet || !token?.poolId) return;
      try {
        const raydium = await loadRaydium(connection, net.cluster, wallet);
        const d = (await raydium.cpmm.getRpcPoolInfo(token.poolId, true)) as unknown as {
          enableCreatorFee?: boolean;
          creatorFeesMintA?: BN;
          creatorFeesMintB?: BN;
        };
        if (!live) return;
        setState({
          enabled: Boolean(d.enableCreatorFee),
          a: d.creatorFeesMintA?.toString() ?? "0",
          b: d.creatorFeesMintB?.toString() ?? "0",
        });
      } catch {
        /* dashboard reports the error; this panel just stays blank */
      }
    })();
    return () => {
      live = false;
    };
  }, [connection, net.cluster, wallet, token]);

  async function claim() {
    if (!wallet || !token?.poolId) return;
    setBusy(true);
    try {
      const raydium = await loadRaydium(connection, net.cluster, wallet);
      const { poolInfo, poolKeys } = await raydium.cpmm.getPoolInfoFromRpc(token.poolId);
      say("info", "Claim creator fees: awaiting wallet approval");
      const { execute } = await raydium.cpmm.collectCreatorFees({
        poolInfo,
        poolKeys,
        txVersion: TxVersion.V0,
        computeBudgetConfig: {
          units: fees.computeUnitLimit,
          microLamports: fees.computeUnitPriceMicroLamports,
        },
      });
      const { txId } = await execute({ sendAndConfirm: true, skipPreflight: false });
      await assertTxSucceeded(connection, txId, "Claim creator fees");
      say("ok", `Creator fee claim sent: ${txId}`);
    } catch (e) {
      say("error", `Fee claim failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!token?.poolId) return null;

  return (
    <div className="card">
      <h2>Creator fees</h2>
      {state && !state.enabled ? (
        <Note kind="warn">
          <p>
            This pool has <code>enableCreatorFee = false</code>, so no creator fee is being charged
            and there is nothing to claim. That is the normal state for a pool opened with{" "}
            <code>createPool</code>; only a permissioned pool
            (<code>createPoolWithPermission</code>) turns it on. Verified against a live swap:
            accrued creator fees stayed at zero while the trader received the full no-creator-fee
            amount.
          </p>
        </Note>
      ) : null}
      <dl className="kv" style={{ marginBottom: 14 }}>
        <dt>Creator fee collection</dt>
        <dd>
          <span className={`pill ${state?.enabled ? "ok" : "warn"}`}>
            {state === null ? "…" : state.enabled ? "enabled" : "disabled"}
          </span>
        </dd>
        <dt>Accrued (raw A / B)</dt>
        <dd data-testid="fee-accrued">
          {state ? `${state.a} / ${state.b}` : "—"}
        </dd>
      </dl>
      <button
        onClick={claim}
        disabled={busy || !state?.enabled}
        data-testid="btn-claimfees"
        title={state?.enabled ? "" : "Nothing to claim on a pool that does not charge a creator fee"}
      >
        {busy ? "Claiming…" : "Claim creator fees"}
      </button>
    </div>
  );
}
